'use client';
import * as React from 'react';
import {
  RuntimeAdapterProvider,
  useAui,
  type RemoteThreadListAdapter,
  type ThreadHistoryAdapter,
} from '@assistant-ui/react';
import { createAssistantStream } from 'assistant-stream';
import { useMemo } from 'react';

const url = `${location.protocol}//${location.hostname}`;

function useThreadListAdapters(headers) {
  const aui = useAui();
  const history = useMemo<ThreadHistoryAdapter>(
    () => ({
      async load() {
        return await new Promise((res) => res({messages: []}));
      },
      async append() {
        return await new Promise((res) => res());
      },
      withFormat: (fmt) => ({
        async load() {
          const { remoteId } = aui.threadListItem.getState();
          if (!remoteId) {
            return { messages: [] };
          }
          const rows = await fetch(
            `${url}:3000/threads/${remoteId}/messages`, { headers }
          ).then((r) => r.json());
          return {
            messages: rows.map((row: any) =>
              fmt.decode({
                id: row.id,
                parent_id: row.parent_id,
                format: row.format,
                content: row.content,
              }),
            ),
          };
        },
        async append(item) {
          const { remoteId } = await aui.threadListItem.initialize();
          await fetch(`${url}:3000/threads/${remoteId}/messages`, {
            method: 'POST',
            body: JSON.stringify({
              id: fmt.getId(item.message),
              parent_id: item.parentId,
              format: fmt.format,
              content: fmt.encode(item),
            }),
            headers,
          });
        },
      }),
    }),
    [aui],
  );
  return useMemo(() => ({ history }), [history]);
}

export const createThreadListAdapter = (headers): RemoteThreadListAdapter => {
  return {
    async list() {
      const rows = await fetch(`${url}:3000/threads`, { headers }).then((r) => r.json());
      return {
        threads: rows.map((t: any) => ({
          status: t.status,
          remoteId: t.id,
          title: t.title ?? undefined,
        })),
      };
    },
    async initialize() {
      const { id } = await fetch(`${url}:3000/threads`, { method: 'POST', headers }).then((r) =>
        r.json(),
      );
      return { remoteId: id };
    },
    async rename(remoteId, title) {
      await fetch(`${url}:3000/threads/${remoteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ title }),
        headers,
      });
    },
    async archive(remoteId) {
      await fetch(`${url}:3000/threads/${remoteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'archived' }),
        headers,
      });
    },
    async unarchive(remoteId) {
      await fetch(`${url}:3000/threads/${remoteId}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'regular' }),
        headers,
      });
    },
    async delete(remoteId) {
      await fetch(`${url}:3000/threads/${remoteId}`, { method: 'DELETE', headers });
    },
    async fetch(remoteId) {
      const t = await fetch(`:3000/threads/${remoteId}`, { headers }).then((r) => r.json());
      return { status: t.status, remoteId: t.id, title: t.title };
    },
    async generateTitle(remoteId, messages) {
      await new Promise((resolve) => {
        resolve([]);
      });
      return createAssistantStream(async (controller) => {
        const { title } = await fetch(`${url}:3000/threads/${remoteId}/title`, {
          method: 'POST',
          body: JSON.stringify({ messages }),
          headers,
        }).then((r) => r.json());
        controller.appendText(title);
      });
    },
    unstable_useAdapters: useThreadListAdapters,
    unstable_Provider({ children }) {
      const adapters = useThreadListAdapters(headers);
      return (
        <RuntimeAdapterProvider adapters={adapters}>
          {children}
        </RuntimeAdapterProvider>
      );
    },
  };
};
